import React, { useContext, useEffect, useState } from "react";
import { StudentContext } from "./StudentLayout";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import { Calendar, CheckCircle2, XCircle } from "lucide-react";

const StudentAttendance = () => {
  const { student } = useContext(StudentContext);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      if (!student?.uid) return;
      try {
        const q = query(
          collection(db, "attendance"),
          where("uid", "==", student.uid)
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        setRecords(data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, [student]);

  const present = records.filter((r) => r.status === "present").length;
  const total = records.length;
  const percent = total > 0 ? Math.round((present / total) * 100) : 0;

  return (
    <div data-testid="student-attendance">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Attendance</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">My Attendance</h1>
        <p className="mt-1 text-slate-600">View your attendance record. Attendance is marked by admin only.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Total Classes" value={total} />
        <Stat label="Present" value={present} color="green" />
        <Stat label="Absent" value={total - present} color="red" />
        <Stat label="Attendance %" value={`${percent}%`} color="blue" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading font-bold text-lg text-slate-900">Attendance Records</h2>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading...</div>
        ) : records.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="no-attendance">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            No attendance records yet. Admin will mark attendance for class days.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-6 py-3 font-semibold">Date</th>
                <th className="text-left px-6 py-3 font-semibold">Status</th>
                <th className="text-left px-6 py-3 font-semibold">Marked By</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-6 py-3 text-slate-900">{r.date}</td>
                  <td className="px-6 py-3">
                    {r.status === "present" ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> Present
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                        <XCircle className="w-3 h-3" /> {r.status || "Absent"}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-slate-500">Admin</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, color = "slate" }) => {
  const colorMap = {
    slate: "text-slate-900",
    green: "text-green-700",
    red: "text-red-700",
    blue: "text-blue-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className={`font-heading text-3xl font-bold ${colorMap[color]}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
};

export default StudentAttendance;
